/**
 * CaseloadView — active clients grouped by owning care manager (PR4-fidelity).
 * A roster: each CM shows a unit LABEL (the all-sees-all visual distinction),
 * the active client count, and the clients. Target and worked hours live on
 * the Team page's capacity tab.
 *
 * TS-6a: client chips link through to /clients/:clientId for
 * view_all_clinical holders — SOAK SCOPING (founder, 2026-07-06): line CMs
 * join at CM rollout; the capability floor (read_clinical_record, all six
 * roles) unchanged — SURFACE gate only (non-holders keep the plain chip).
 *
 * A demo client is listed, with a neutral "Demo client" chip after its
 * name, and counted in "N active": the note pickers need it here. The
 * Capacity tab's active count leaves demo clients out, and the chip says
 * why this list can hold more.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { Chip } from '@/components/primitives/Chip';
import { canOpenEveryClientRecord } from '@/lib/capabilities';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SkeletonRow } from '@/components/Skeleton';
import { avatarClasses, initials } from './workspaceFormat';
import type { CaseloadCmWire } from '@/lib/api';

export interface CaseloadViewProps {
  careManagers: CaseloadCmWire[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry?: () => void;
}

export function CaseloadView({ careManagers, isLoading, isError, error, onRetry }: CaseloadViewProps): ReactNode {
  // Hooks above the early returns (the #310 discipline holds even in
  // sub-page components).
  const { tenantSlug, whoami } = useTenant();
  const linkToRecord = canOpenEveryClientRecord(whoami);
  if (isLoading) return (<div><SkeletonRow /><SkeletonRow /></div>);
  if (isError) return <ErrorBanner error={error} onRetry={onRetry} what="the caseload" />;

  return (
    <div className="max-w-3xl">
      <div className="bg-paper rounded-card overflow-hidden shadow-sh2" data-testid="caseload-roster">
        {careManagers.map((cm) => (
          <div key={cm.roster_id} data-testid="caseload-cm" className="px-4 py-3.5 border-b border-sage-800/[8%] last:border-b-0">
            <div className="flex items-center gap-3">
              <span className={avatarClasses(true)}>{initials(cm.full_name)}</span>
              <span className="font-semibold text-sage-800">{cm.full_name}</span>
              {cm.unit_name && (
                <span className="text-[10.5px] font-semibold text-warm-700 bg-warm-100 rounded-pill px-2 py-0.5">{cm.unit_name}</span>
              )}
              <span className="ml-auto text-[13px] font-semibold text-sage-400">{cm.active_client_count} active</span>
            </div>
            {cm.clients.length > 0 && (
              <div className="mt-2 pl-9 flex flex-wrap gap-1.5">
                {cm.clients.map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-1" data-testid="caseload-client">
                    {linkToRecord ? (
                      <Link
                        to={`/t/${tenantSlug}/clients/${c.id}`}
                        data-testid="caseload-client-link"
                        className="text-xs text-sage-700 bg-cream rounded-pill px-2 py-0.5 no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                      >
                        {c.display_name}
                      </Link>
                    ) : (
                      <span className="text-xs text-ink-soft bg-cream rounded-pill px-2 py-0.5">{c.display_name}</span>
                    )}
                    {c.is_demo && (
                      <Chip variant="neutral" data-testid="caseload-client-demo">
                        Demo client
                      </Chip>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {careManagers.length === 0 && <p className="px-4 py-4 text-sm text-ink-soft">Mark who does care management on the People page and you'll see them here.</p>}
      </div>
    </div>
  );
}
